import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell } from
'recharts';
import {
  Plus,
  Target,
  Globe,
  Users,
  TrendingUp,
  Calendar,
  RefreshCw,
  ArrowRight,
  Link2,
  Copy,
  ExternalLink,
  MousePointer,
  Eye,
  FileText,
  Video,
  Mail,
  Share2,
  BarChart3,
  Clock,
  Zap,
  Code,
  Settings,
  Play,
  Pause } from
'lucide-react';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
};

const ASSET_ICONS = {
  landing_page: Globe,
  email: Mail,
  pdf: FileText,
  video: Video,
  social_post: Share2,
  ad: Target
};

const CHART_COLORS = ['#14b8a6', '#3b82f6', '#f97316', '#8b5cf6', '#22c55e', '#ef4444'];

export const CampaignIntelligence = () => {const { t } = useTranslation("crm");
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [trackingLinks, setTrackingLinks] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewCampaignDialog, setShowNewCampaignDialog] = useState(false);
  const [showNewLinkDialog, setShowNewLinkDialog] = useState(false);
  const [showTrackingScriptDialog, setShowTrackingScriptDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [trackingScript, setTrackingScript] = useState('');

  // New campaign form
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    target_modules: [],
    target_industries: [],
    tracking_domain: '',
    enable_website_tracking: true,
    enable_email_tracking: true,
    enable_social_tracking: true,
    start_date: '',
    end_date: '',
    budget: ''
  });

  // New tracking link form
  const [newLink, setNewLink] = useState({
    campaign_id: '',
    destination_url: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, statsRes, analyticsRes, linksRes] = await Promise.all([
      fetch(`${API_URL}/api/campaign-intelligence/campaigns`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/stats`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/analytics/overview?days=30`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/tracking-links`, { headers: getAuthHeaders() })]
      );

      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (linksRes.ok) setTrackingLinks(await linksRes.json());
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign-intelligence/campaigns`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newCampaign,
          budget: newCampaign.budget ? parseFloat(newCampaign.budget) : null
        })
      });

      if (!res.ok) throw new Error('Failed to create campaign');

      toast.success('Campaign created successfully');
      setShowNewCampaignDialog(false);
      setNewCampaign({
        name: '',
        description: '',
        target_modules: [],
        target_industries: [],
        tracking_domain: '',
        enable_website_tracking: true,
        enable_email_tracking: true,
        enable_social_tracking: true,
        start_date: '',
        end_date: '',
        budget: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const createTrackingLink = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign-intelligence/tracking-links`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newLink,
          campaign_id: parseInt(newLink.campaign_id)
        })
      });

      if (!res.ok) throw new Error('Failed to create tracking link');

      const data = await res.json();
      toast.success('Tracking link created');
      setShowNewLinkDialog(false);
      setNewLink({
        campaign_id: '',
        destination_url: '',
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_content: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getTrackingScript = async (campaignId) => {
    try {
      const res = await fetch(`${API_URL}/api/campaign-intelligence/tracking-script/${campaignId}`, {
        headers: getAuthHeaders()
      });

      if (!res.ok) throw new Error('Failed to get tracking script');

      const data = await res.json();
      setTrackingScript(data.script);
      setShowTrackingScriptDialog(true);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      const res = await fetch(`${API_URL}/api/campaign-intelligence/campaigns/${campaign.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });

      if (!res.ok) throw new Error('Failed to update campaign');

      toast.success(`Campaign ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="campaign-intelligence">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("campaignIntelligence", "Campaign Intelligence")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("trackAnalyzeAnd", "Track, analyze, and optimize your marketing campaigns")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShowNewLinkDialog(true)}>
            
            <Link2 className="h-4 w-4" />{t("newLink", "New Link")}

          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setTrackingScript('<script src=\"https://ezerp.pro/tracking.js\" data-campaign=\"demo\"></script>');
              setShowTrackingScriptDialog(true);
            }}>

            <Code className="h-4 w-4" />{t("trackingScript", "Tracking Script")}

          </Button>
          <Button
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={() => setShowNewCampaignDialog(true)}>
            
            <Plus className="h-4 w-4" />{t("newCampaign", "New Campaign")}

          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("activeCampaigns", "Active Campaigns")}</p>
                <p className="text-2xl font-bold">{stats.active_campaigns || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Users className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalVisitors", "Total Visitors")}</p>
                <p className="text-2xl font-bold">{stats.total_visitors || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <MousePointer className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalClicks", "Total Clicks")}</p>
                <p className="text-2xl font-bold">{stats.total_clicks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Zap className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalEvents", "Total Events")}</p>
                <p className="text-2xl font-bold">{stats.total_events || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-[600px]">
          <TabsTrigger value="campaigns" className="gap-2">
            <Target className="h-4 w-4" />{t("campaigns", "Campaigns")}

          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />{t("analytics", "Analytics")}

          </TabsTrigger>
          <TabsTrigger value="links" className="gap-2">
            <Link2 className="h-4 w-4" />{t("links", "Links")}

          </TabsTrigger>
          <TabsTrigger value="tracking" className="gap-2">
            <Code className="h-4 w-4" />{t("tracking", "Tracking")}

          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          {campaigns.length === 0 ?
          <Card className="card-elevated">
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t("noMarketingCampaigns", "No Marketing Campaigns")}</h3>
                <p className="text-muted-foreground mb-4">{t("createYourFirst", "Create your first campaign to start tracking")}</p>
                <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowNewCampaignDialog(true)}>
                
                  <Plus className="mr-2 h-4 w-4" />{t("createCampaign", "Create Campaign")}

              </Button>
              </CardContent>
            </Card> :

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {campaigns.map((campaign) =>
            <Card key={campaign.id} className="card-elevated hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{campaign.name}</CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {campaign.description || 'No description'}
                        </CardDescription>
                      </div>
                      <Badge className={statusColors[campaign.status]}>
                        {campaign.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Tracking Config */}
                    <div className="flex gap-2">
                      {campaign.enable_website_tracking &&
                  <Badge variant="outline" className="text-xs">
                          <Globe className="h-3 w-3 mr-1" />{t("website", "Website")}

                  </Badge>
                  }
                      {campaign.enable_email_tracking &&
                  <Badge variant="outline" className="text-xs">
                          <Mail className="h-3 w-3 mr-1" />{t("email", "Email")}

                  </Badge>
                  }
                      {campaign.enable_social_tracking &&
                  <Badge variant="outline" className="text-xs">
                          <Share2 className="h-3 w-3 mr-1" />{t("social", "Social")}

                  </Badge>
                  }
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-2 text-center pt-2 border-t">
                      <div>
                        <p className="text-lg font-bold">{campaign.visitor_count || 0}</p>
                        <p className="text-xs text-muted-foreground">{t("visitors", "Visitors")}</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-orange-600">{campaign.total_clicks || 0}</p>
                        <p className="text-xs text-muted-foreground">{t("clicks", "Clicks")}</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-blue-600">{campaign.asset_count || 0}</p>
                        <p className="text-xs text-muted-foreground">{t("assets", "Assets")}</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-teal-600">{campaign.link_count || 0}</p>
                        <p className="text-xs text-muted-foreground">{t("links", "Links")}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                    size="sm"
                    variant={campaign.status === 'active' ? 'outline' : 'default'}
                    className={campaign.status === 'active' ? '' : 'bg-blue-600 hover:bg-blue-700'}
                    onClick={() => toggleCampaignStatus(campaign)}>
                    
                        {campaign.status === 'active' ?
                    <><Pause className="h-4 w-4 mr-1" />{t("pause", "Pause")}</> :

                    <><Play className="h-4 w-4 mr-1" />{t("activate", "Activate")}</>
                    }
                      </Button>
                      <Button
                    size="sm"
                    variant="outline"
                    onClick={() => getTrackingScript(campaign.id)}>
                    
                        <Code className="h-4 w-4 mr-1" />{t("script", "Script")}

                  </Button>
                      <Button size="sm" variant="outline">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
            )}
            </div>
          }
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Daily Activity Chart */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />{t("dailyActivityLast", "Daily Activity (Last 30 Days)")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {analytics.daily_breakdown && analytics.daily_breakdown.length > 0 ?
                  <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analytics.daily_breakdown}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis
                        dataKey="date"
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        className="text-xs" />
                      
                        <YAxis className="text-xs" />
                        <Tooltip
                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }} />
                      
                        <Area
                        type="monotone"
                        dataKey="sessions"
                        stackId="1"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.3}
                        name="Sessions" />
                      
                        <Area
                        type="monotone"
                        dataKey="visitors"
                        stackId="2"
                        stroke="#14b8a6"
                        fill="#14b8a6"
                        fillOpacity={0.3}
                        name="Visitors" />
                      
                      </AreaChart>
                    </ResponsiveContainer> :

                  <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center">
                        <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>{t("noDataAvailable", "No data available yet")}</p>
                        <p className="text-sm">{t("startTrackingTo", "Start tracking to see analytics")}</p>
                      </div>
                    </div>
                  }
                </div>
              </CardContent>
            </Card>

            {/* Summary Stats */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-muted-foreground" />{t("analyticsSummary", "Analytics Summary")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-blue-500/10">
                      <p className="text-3xl font-bold text-blue-600">
                        {analytics.summary?.total_visitors || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">{t("totalVisitors", "Total Visitors")}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-teal-500/10">
                      <p className="text-3xl font-bold text-teal-600">
                        {analytics.summary?.total_sessions || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">{t("totalSessions", "Total Sessions")}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-orange-500/10">
                      <p className="text-3xl font-bold text-orange-600">
                        {analytics.summary?.page_views || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">{t("pageViews", "Page Views")}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-500/10">
                      <p className="text-3xl font-bold text-purple-600">
                        {analytics.summary?.clicks || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">{t("clicks", "Clicks")}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">{t("avgSessionDuration", "Avg. Session Duration")}</span>
                      <span className="font-semibold">{analytics.summary?.avg_session_duration || 0}s</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">{t("avgPagessession", "Avg. Pages/Session")}</span>
                      <span className="font-semibold">{analytics.summary?.avg_pages_per_session || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">{t("conversions", "Conversions")}</span>
                      <span className="font-semibold text-green-600">{analytics.summary?.conversions || 0}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tracking Links Tab */}
        <TabsContent value="links" className="space-y-4">
          <Card className="card-elevated">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("campaign", "Campaign")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("trackingUrl", "Tracking URL")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("utmSource", "UTM Source")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("clicks", "Clicks")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("unique", "Unique")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackingLinks.map((link) =>
                    <tr key={link.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-3 px-4">
                          <p className="font-medium">{link.campaign_name}</p>
                          <p className="text-xs text-muted-foreground">{link.asset_name || 'No asset'}</p>
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {API_URL}{t("apicampaignintelligence", "/api/campaign-intelligence")}{link.tracking_url}
                          </code>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{link.utm_source || 'direct'}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-lg font-bold text-orange-600">{link.click_count}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-lg font-bold text-blue-600">{link.unique_clicks}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => copyToClipboard(`${API_URL}/api/campaign-intelligence${link.tracking_url}`)}>
                            
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => window.open(link.destination_url, '_blank')}>
                            
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {trackingLinks.length === 0 &&
              <div className="py-12 text-center">
                  <Link2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("noTrackingLinks", "No Tracking Links")}</h3>
                  <p className="text-muted-foreground mb-4">{t("createTrackingLinks", "Create tracking links to measure campaign performance")}</p>
                  <Button
                  variant="outline"
                  onClick={() => setShowNewLinkDialog(true)}>
                  
                    <Plus className="mr-2 h-4 w-4" />{t("createTrackingLink", "Create Tracking Link")}

                </Button>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tracking Setup Tab */}
        <TabsContent value="tracking" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>{t("websiteTrackingSetup", "Website Tracking Setup")}</CardTitle>
              <CardDescription>{t("addTheTracking", "Add the tracking script to your website to collect visitor analytics")}

              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                {campaigns.filter((c) => c.status === 'active').map((campaign) =>
                <div
                  key={campaign.id}
                  className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => getTrackingScript(campaign.id)}>
                  
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{campaign.name}</h4>
                        <p className="text-sm text-muted-foreground">{t("clickToGet", "Click to get tracking script")}</p>
                      </div>
                      <Code className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                )}
              </div>

              {campaigns.filter((c) => c.status === 'active').length === 0 &&
              <div className="py-12 text-center">
                  <Code className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("noActiveCampaigns", "No Active Campaigns")}</h3>
                  <p className="text-muted-foreground">{t("activateACampaign", "Activate a campaign to get tracking scripts")}</p>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Campaign Dialog */}
      <Dialog open={showNewCampaignDialog} onOpenChange={setShowNewCampaignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("createMarketingCampaign", "Create Marketing Campaign")}</DialogTitle>
            <DialogDescription>{t("setUpTracking", "Set up tracking for your marketing initiatives")}

            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">{t("campaignName", "Campaign Name *")}</Label>
              <Input
                id="name"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                placeholder="e.g., Q1 Product Launch" />
              
            </div>

            <div>
              <Label htmlFor="description">{t("description", "Description")}</Label>
              <Input
                id="description"
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                placeholder="Campaign goals and target audience" />
              
            </div>

            <div>
              <Label>{t("trackingDomainOptional", "Tracking Domain (optional)")}</Label>
              <Input
                value={newCampaign.tracking_domain}
                onChange={(e) => setNewCampaign({ ...newCampaign, tracking_domain: e.target.value })}
                placeholder="e.g., track.yourdomain.com" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("startDate", "Start Date")}</Label>
                <Input
                  type="date"
                  value={newCampaign.start_date}
                  onChange={(e) => setNewCampaign({ ...newCampaign, start_date: e.target.value })} />
                
              </div>
              <div>
                <Label>{t("endDate", "End Date")}</Label>
                <Input
                  type="date"
                  value={newCampaign.end_date}
                  onChange={(e) => setNewCampaign({ ...newCampaign, end_date: e.target.value })} />
                
              </div>
            </div>

            <div>
              <Label>{t("budgetOptional", "Budget (optional)")}</Label>
              <Input
                type="number"
                value={newCampaign.budget}
                onChange={(e) => setNewCampaign({ ...newCampaign, budget: e.target.value })}
                placeholder="Campaign budget" />
              
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCampaignDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={createCampaign}
              disabled={!newCampaign.name}>{t("createCampaign", "Create Campaign")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Tracking Link Dialog */}
      <Dialog open={showNewLinkDialog} onOpenChange={setShowNewLinkDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("createTrackingLink", "Create Tracking Link")}</DialogTitle>
            <DialogDescription>{t("generateATrackable", "Generate a trackable link for your campaign")}

            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>{t("campaign", "Campaign *")}</Label>
              <Select
                value={newLink.campaign_id}
                onValueChange={(value) => setNewLink({ ...newLink, campaign_id: value })}>
                
                <SelectTrigger>
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) =>
                  <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t("destinationUrl", "Destination URL *")}</Label>
              <Input
                value={newLink.destination_url}
                onChange={(e) => setNewLink({ ...newLink, destination_url: e.target.value })}
                placeholder="https://yoursite.com/landing-page" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("utmSource", "UTM Source")}</Label>
                <Input
                  value={newLink.utm_source}
                  onChange={(e) => setNewLink({ ...newLink, utm_source: e.target.value })}
                  placeholder="e.g., facebook" />
                
              </div>
              <div>
                <Label>{t("utmMedium", "UTM Medium")}</Label>
                <Input
                  value={newLink.utm_medium}
                  onChange={(e) => setNewLink({ ...newLink, utm_medium: e.target.value })}
                  placeholder="e.g., social" />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("utmCampaign", "UTM Campaign")}</Label>
                <Input
                  value={newLink.utm_campaign}
                  onChange={(e) => setNewLink({ ...newLink, utm_campaign: e.target.value })}
                  placeholder="e.g., spring_sale" />
                
              </div>
              <div>
                <Label>{t("utmContent", "UTM Content")}</Label>
                <Input
                  value={newLink.utm_content}
                  onChange={(e) => setNewLink({ ...newLink, utm_content: e.target.value })}
                  placeholder="e.g., banner_1" />
                
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLinkDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={createTrackingLink}
              disabled={!newLink.campaign_id || !newLink.destination_url}>{t("createLink", "Create Link")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tracking Script Dialog */}
      <Dialog open={showTrackingScriptDialog} onOpenChange={setShowTrackingScriptDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("trackingScript", "Tracking Script")}</DialogTitle>
            <DialogDescription>{t("addThisScript", "Add this script to your website to enable analytics tracking")}

            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="relative">
              <pre className="p-4 rounded-lg bg-muted overflow-x-auto text-xs max-h-[400px]">
                <code>{trackingScript}</code>
              </pre>
              <Button
                size="sm"
                variant="outline"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(trackingScript)}>
                
                <Copy className="h-4 w-4 mr-1" />{t("copy", "Copy")}

              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrackingScriptDialog(false)}>{t("close", "Close")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default CampaignIntelligence;
